/**
 * CopyButton — small clipboard-copy button for debug tool windows.
 *
 * Each debug tool drops one at the top of its content. The `getText`
 * callback runs at click-time (not render-time) so we copy the LATEST
 * snapshot, not a stale closure. Visual feedback: flashes "Copied!" for
 * 1.5s, then reverts. Failure paths (clipboard permission denied) flash
 * "Copy failed" so DJ knows to retry.
 *
 * Style matches the existing CombatLogPanel copy button (palette + sizes)
 * so debug tools feel consistent.
 *
 * LRG.7 (2026-05-13) — added to every DebugHUDHost tool.
 */

import React, { useCallback, useState } from 'react';
import { gameLogger } from '../../../utils/GameLogger';

interface CopyButtonProps {
  /** Callable that returns the text to copy. Invoked at click-time so
   *  rapidly-changing content (FPS counter, packet rates) copies what
   *  the user sees right now. */
  getText: () => string;
  /** Optional label override. Defaults to "📋 Copy". */
  label?: string;
  /** Optional hover hint. Defaults to "Copy panel contents to clipboard". */
  title?: string;
}

const BASE_STYLE: React.CSSProperties = {
  padding: '2px 8px',
  fontSize: 10,
  fontFamily: 'monospace',
  border: '1px solid rgba(255,255,255,0.2)',
  borderRadius: 3,
  background: 'rgba(255,255,255,0.05)',
  color: '#aaa',
  cursor: 'pointer',
  marginBottom: 4,
  userSelect: 'none',
};

const FLASH_OK_STYLE: React.CSSProperties = {
  ...BASE_STYLE,
  background: 'rgba(66, 215, 125, 0.2)',
  borderColor: '#42d77d',
  color: '#42d77d',
};

const FLASH_FAIL_STYLE: React.CSSProperties = {
  ...BASE_STYLE,
  background: 'rgba(255, 95, 95, 0.2)',
  borderColor: '#ff5f5f',
  color: '#ff5f5f',
};

export const CopyButton: React.FC<CopyButtonProps> = ({
  getText,
  label = '📋 Copy',
  title = 'Copy panel contents to clipboard',
}) => {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');

  const copy = useCallback(() => {
    let text: string;
    try {
      text = getText();
    } catch (err) {
      gameLogger.warn('copy', '[CopyButton] getText threw', { err: String(err) });
      setState('fail');
      setTimeout(() => setState('idle'), 1500);
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      setState('fail');
      setTimeout(() => setState('idle'), 1500);
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => {
        setState('ok');
        setTimeout(() => setState('idle'), 1500);
      },
      (err) => {
        gameLogger.warn('copy', '[CopyButton] clipboard write rejected', { err: String(err?.message ?? err) });
        setState('fail');
        setTimeout(() => setState('idle'), 1500);
      },
    );
  }, [getText]);

  const style = state === 'ok' ? FLASH_OK_STYLE : state === 'fail' ? FLASH_FAIL_STYLE : BASE_STYLE;
  const displayLabel = state === 'ok' ? '✓ Copied' : state === 'fail' ? '✗ Failed' : label;

  return (
    <button
      data-testid="debug-copy-button"
      data-copy-state={state}
      onClick={copy}
      style={style}
      title={title}
      type="button"
    >
      {displayLabel}
    </button>
  );
};
